import type { Quote } from '@/gen/cortado/v1/invoices_pb'
import { QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { formatCurrencyWhole } from '@/lib/currency-utils'
import { formatShortDate } from '@/lib/date-utils'
import type { PipelinePhaseSegmentState } from '@/components/pipeline/pipeline-job-view'
import type { PipelineStageTone } from '@/components/pipeline/pipeline-stage-cell'

export interface QuoteStageCell {
  label: string
  state: PipelinePhaseSegmentState
  title: string
  subtitle: string
  tone: PipelineStageTone
}

const SENT_STATUSES = new Set([
  QuoteStatusEnum.QUOTE_STATUS_SENT,
  QuoteStatusEnum.QUOTE_STATUS_VIEWED,
  QuoteStatusEnum.QUOTE_STATUS_REVISED,
  QuoteStatusEnum.QUOTE_STATUS_ACCEPTED,
  QuoteStatusEnum.QUOTE_STATUS_REJECTED,
])

function decision(quote: Quote): Pick<QuoteStageCell, 'title' | 'subtitle' | 'tone'> | undefined {
  switch (quote.status) {
    case QuoteStatusEnum.QUOTE_STATUS_ACCEPTED:
      return { title: 'Accepted', subtitle: formatShortDate(quote.acceptedAt) || '—', tone: 'default' }
    case QuoteStatusEnum.QUOTE_STATUS_REJECTED:
      return { title: 'Declined', subtitle: formatShortDate(quote.rejectedAt) || '—', tone: 'negative' }
    case QuoteStatusEnum.QUOTE_STATUS_EXPIRED:
      return {
        title: 'Expired',
        subtitle: quote.validUntil ? `Valid until ${formatShortDate(quote.validUntil)}` : '—',
        tone: 'negative',
      }
    case QuoteStatusEnum.QUOTE_STATUS_CANCELLED:
      return { title: 'Cancelled', subtitle: '—', tone: 'negative' }
    default:
      return undefined
  }
}

/**
 * The quote page's draft / sent / viewed / decision stepper, mirroring the job
 * page's lead / quote / booking strip. `quote` is undefined for a new quote
 * that hasn't been saved yet.
 */
export function quoteStageCells(quote: Quote | undefined): QuoteStageCell[] {
  const decided = quote ? decision(quote) : undefined
  const isSent = Boolean(quote?.sentAt) || (quote ? SENT_STATUSES.has(quote.status) : false)
  const isViewed = Boolean(quote?.viewedAt) || quote?.status === QuoteStatusEnum.QUOTE_STATUS_VIEWED
  // The first unfinished step; past the end once the customer has decided.
  const current = decided ? 4 : isViewed ? 3 : isSent ? 2 : 0
  const state = (index: number): PipelinePhaseSegmentState =>
    index < current ? 'done' : index === current ? 'current' : 'future'

  const draft: QuoteStageCell = {
    label: 'Draft',
    state: state(0),
    title: quote ? `Quote #${quote.quoteNumber}` : 'New quote',
    subtitle: quote ? `$${formatCurrencyWhole(Number(quote.totalCents))}` : 'Not saved yet',
    tone: 'default',
  }

  const sent: QuoteStageCell = isSent
    ? {
        label: 'Sent',
        state: state(1),
        title: quote?.status === QuoteStatusEnum.QUOTE_STATUS_REVISED ? 'Sent · revised' : 'Sent',
        subtitle: formatShortDate(quote?.sentAt) || '—',
        tone: 'default',
      }
    : { label: 'Sent', state: state(1), title: 'Not sent', subtitle: 'Email it to the customer', tone: 'default' }

  const viewed: QuoteStageCell = isViewed
    ? { label: 'Viewed', state: state(2), title: 'Viewed', subtitle: formatShortDate(quote?.viewedAt) || '—', tone: 'default' }
    : {
        label: 'Viewed',
        state: state(2),
        title: decided ? 'Not viewed' : 'Not viewed yet',
        subtitle: decided ? '—' : isSent ? 'Waiting on the customer' : 'Opens in the customer portal',
        tone: 'default',
      }

  const decisionCell: QuoteStageCell = decided
    ? { label: 'Decision', state: state(3), ...decided }
    : {
        label: 'Decision',
        state: state(3),
        title: 'Awaiting decision',
        subtitle: quote?.validUntil ? `Valid until ${formatShortDate(quote.validUntil)}` : 'No expiry date',
        tone: 'default',
      }

  return [draft, sent, viewed, decisionCell]
}
